import gql from "graphql-tag";

import {isTruthy} from "@khanacademy/wonder-stuff-core";
import type {ApolloGqlClient} from "@repo/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import Log, {Errors} from "@repo/logging";
import {RequestInfo} from "@repo/shared-core";

import type {
    GetSubtitles,
    GetSubtitlesVariables,
} from "./__graphql-types__/GetSubtitles.ts";
import {mapGqlVideoCaption} from "./gql-type-mappers.ts";
import type {VideoCaption} from "./types.ts";

/**
 * Formats numbers as video timestamps
 * e.g. msToTime(1999) === "00:00:01.999"
 *
 * Copied from https://stackoverflow.com/a/9763769
 */
export function msToTime(s: number): string {
    // Pad to 2 or 3 digits, default is 2
    function pad(n: number, z = 2) {
        return ("00" + n).slice(-z);
    }

    const ms = s % 1000;
    const remainderWithoutMS = (s - ms) / 1000;
    const secs = remainderWithoutMS % 60;
    const remainderWithoutS = (remainderWithoutMS - secs) / 60;
    const mins = remainderWithoutS % 60;
    const hrs = (remainderWithoutS - mins) / 60;

    return pad(hrs) + ":" + pad(mins) + ":" + pad(secs) + "." + pad(ms, 3);
}

/**
 * Converts an array of captions to a string that follows the format of a VTT file.
 * For the exact format, see https://developer.mozilla.org/en-US/docs/Web/API/WebVTT_API
 *
 * captions an array of captions
 */
export function convertCaptionsToVTT(
    captions: ReadonlyArray<VideoCaption>,
): string {
    const captionsString = captions
        .map(
            (caption, index) =>
                `${index}\n${msToTime(caption.startTime)} --> ${msToTime(
                    caption.endTime,
                )}\n<c.caption>${caption.text}</c>`,
        )
        .join("\n\n");

    return `WEBVTT

${captionsString}`;
}

/**
 * Creates a blob for a string that holds the content of a vtt file, and returns it as a url to be used as a src.
 *
 * vttString the contents of a VTT file as a string
 */
export function generateUrlForVTTString(vttString: string): string {
    const trackBlob = new Blob([vttString], {
        type: "text/plain;charset=utf-8",
    });

    return URL.createObjectURL(trackBlob);
}

/**
 * Fetches captions and returns a `src` of a vtt file.
 * translatedYoutubeId the youtube id for the desired captions
 */
export async function fetchFallbackPlayerCaptions(
    gqlClient: ApolloGqlClient,
    translatedYoutubeId: string,
): Promise<string | null | undefined> {
    const captions = await getSubtitlesForVideo(
        gqlClient,
        translatedYoutubeId,
        RequestInfo.kaLocale,
    );

    // captions.length === 0 on error
    if (captions.length === 0) {
        return null;
    }

    const vttContent = convertCaptionsToVTT(captions);
    return generateUrlForVTTString(vttContent);
}

export const GET_SUBTITLES: GqlOperation<GetSubtitles, GetSubtitlesVariables> =
    gqlOp(gql`
        query GetSubtitles($youtubeId: String!, $kaLocale: String!) {
            subtitles(youtubeId: $youtubeId, kaLocale: $kaLocale) {
                text
                startTime
                endTime
                kaIsValid
            }
        }
    `);

export const getSubtitlesForVideo = (
    gqlClient: ApolloGqlClient,
    youtubeId: string,
    kaLocale: string,
): Promise<ReadonlyArray<VideoCaption>> => {
    return gqlClient
        .query(GET_SUBTITLES, {
            variables: {youtubeId: youtubeId, kaLocale: kaLocale},
            fetchPolicy: "cache-first",
        })
        .then((response) =>
            mapGqlVideoCaption(response.data?.subtitles.filter(isTruthy) ?? []),
        )
        .catch((err) => {
            Log.error(`Failed to get subtitles for video`, Errors.Internal, {
                cause: err,
                sentryData: {
                    contexts: {
                        extras: {
                            youtubeId,
                            kaLocale,
                        },
                    },
                },
            });
            return [];
        });
};
